import { useTranslation } from 'react-i18next';
import {
  CalendarOutlined,
  CameraOutlined,
  EditOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import { EMPTY_DISPLAY } from '@/shared/history';
import { DATE_FORMATS, formatDate } from '@/utils/formUtils';
import { getUserFullName } from '@/utils/utils';

const initials = (user) =>
  user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : '·';

function Stat({ icon, label, value, hint }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5 rounded-xl border border-zinc-200 bg-white p-2.5">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[rgba(var(--accentColor),0.1)] text-base text-accent">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[11px] font-semibold uppercase tracking-[0.06em] text-zinc-400">
          {label}
        </span>
        <span className="block truncate text-[15px] font-extrabold text-zinc-900">
          {value}
        </span>
        {hint && (
          <span className="block truncate text-[11px] text-zinc-500">
            {hint}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * Profile page: the history's key numbers above the list — when the record
 * was created, when it last changed, how many changes and snapshots, and who
 * changed it most.
 *
 * @param {Object} props
 * @param {Object|null} props.data - history statistics
 * @param {boolean} props.canViewSnapshots
 * @param {string} [props.createdAt]
 */
export default function HistorySummary({ data, canViewSnapshots, createdAt }) {
  const { t } = useTranslation(['history', 'translations']);
  const asDate = (value) =>
    value ? formatDate(value, DATE_FORMATS.DISPLAY_DOTS) : EMPTY_DISPLAY;
  const users = data?.mostActiveUsers || [];

  return (
    <div className="grid gap-3">
      <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
        <Stat
          icon={<CalendarOutlined />}
          label={t('employeeProfile.createdAt', { ns: 'translations' })}
          value={asDate(createdAt)}
        />
        <Stat
          icon={<HistoryOutlined />}
          label={t('ui.lastModification')}
          value={asDate(data?.lastModification)}
          hint={
            data?.firstModification
              ? `${t('ui.firstModification')}: ${asDate(data.firstModification)}`
              : null
          }
        />
        <Stat
          icon={<EditOutlined />}
          label={t('ui.totalChanges')}
          value={data?.totalChanges ?? EMPTY_DISPLAY}
        />
        {canViewSnapshots && (
          <Stat
            icon={<CameraOutlined />}
            label={t('ui.totalSnapshots')}
            value={data?.totalSnapshots ?? EMPTY_DISPLAY}
          />
        )}
      </div>

      {users.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-zinc-400">
            {t('ui.mostActiveUsers')}
          </span>
          {users.map((item, index) => {
            const name = item.user
              ? getUserFullName(item.user.firstName, item.user.lastName)
              : t('ui.system');
            return (
              <Tooltip
                key={item.user?._id || index}
                title={t('ui.changeCount', { count: item.count })}
              >
                <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white py-0.5 pl-0.5 pr-2.5 text-[12px] font-semibold text-zinc-700">
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-accent text-[9px] font-bold text-white">
                    {initials(item.user)}
                  </span>
                  {name}
                  <span className="text-zinc-400">{item.count}</span>
                </span>
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
}
